import { useEffect, useState } from "react";
import { Clock, Droplets, Heart } from "lucide-react";
import lotusImage from "@/assets/lotus-flower.jpeg";
import { SubtleWaves } from "@/components/ui/waves";
import teapotInfusion from "@/assets/teapot-infusion.jpg";
import goldenScoopCloseup from "@/assets/golden-scoop-closeup.jpg";
import pouringTeaRitual from "@/assets/pouring-tea-ritual.jpg";

export const Ritual = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const steps = [
    {
      icon: Droplets,
      title: "Bereid met aandacht",
      description: "Gebruik 1-2 theelepels blauwe lotus per kopje. Giet er heet (niet kokend) water overheen.",
      time: "2 min",
      image: teapotInfusion,
    },
    {
      icon: Clock,
      title: "Laat trekken met intentie",
      description: "Laat de thee 5-10 minuten trekken. Hoe langer, hoe dieper de smaak en de kalmte.",
      time: "5-10 min",
      image: goldenScoopCloseup,
    },
    {
      icon: Heart,
      title: "Geniet bewust",
      description: "Drink langzaam en voel hoe de kalmte door je lichaam stroomt. Perfect voor meditatie.",
      time: "15 min",
      image: pouringTeaRitual,
    },
  ];

  return (
    <section className="relative py-24 overflow-hidden section-black">
      {/* Premium Starfield Background */}
      <div className="absolute inset-0">
        <SubtleWaves className="opacity-20" />
      </div>
      
      {/* Refined Parallax Stars */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          transform: `translateY(${scrollY * 0.3}px)`,
        }}
      >
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-pulse"
            style={{
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              background: i % 4 === 0 
                ? 'hsl(var(--gold-glow))' 
                : i % 4 === 1 
                  ? 'hsl(var(--purple-accent))' 
                  : i % 4 === 2
                    ? 'hsl(var(--blue-accent))'
                    : 'hsl(var(--gold))',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${1 + Math.random() * 2}s`,
              boxShadow: '0 0 4px currentColor',
              opacity: 0.6,
            }}
          />
        ))}
      </div>

      {/* Premium Moving Moon */}
      <div 
        className="absolute top-20 right-20 w-40 h-40 rounded-full bg-gradient-gold opacity-20 luxury-glow"
        style={{
          transform: `translateY(${scrollY * 0.2}px) translateX(${scrollY * 0.1}px)`,
          animation: 'luxury-pulse 5s ease-in-out infinite',
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-gold text-glow mb-6 animate-on-scroll">
            Het Lunssence Ritueel
          </h2>
          
          <div className="w-20 h-1 bg-gradient-gold mx-auto mb-8 animate-on-scroll [animation-delay:0.2s] rounded-full shadow-gold-glow" />
          
          <p className="text-lg text-background/80 max-w-2xl mx-auto animate-on-scroll [animation-delay:0.4s]">
            Meer dan thee drinken – het is een moment van verstilling, een brug naar je innerlijke rust
          </p>
        </div>

        {/* Ritual Steps with Photos */}
        <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto mb-16">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                className="group text-center animate-on-scroll fade-in-on-scroll"
                style={{ animationDelay: `${index * 0.3}s` }}
              >
                <div className="relative">
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-gold rounded-full flex items-center justify-center text-black font-bold text-sm shadow-gold z-10">
                    {index + 1}
                  </div>
                  
                  {/* Step Image */}
                  <div className="mb-6 overflow-hidden rounded-xl relative aspect-[4/3]">
                    <img 
                      src={step.image} 
                      alt={step.title}
                      className="w-full h-full object-cover rounded-xl shadow-md transition-all duration-700 group-hover:scale-110"
                    />
                  </div>

                  
                  {/* Icon Circle */}
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold/10 border-2 border-gold/40 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 backdrop-blur-sm luxury-glow">
                    <IconComponent className="w-10 h-10 text-gold" />
                  </div>
                  
                  {/* Time Badge */}
                  <div className="inline-block px-3 py-1 mb-4 bg-gold/20 rounded-full text-gold text-sm font-semibold border border-gold/40">
                    {step.time}
                  </div>
                  
                  <h3 className="font-serif text-xl font-bold text-gold mb-4">
                    {step.title}
                  </h3>
                  
                  <p className="text-background/80 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tea Cup with Steam Animation */}
        <div className="text-center mb-16 animate-on-scroll [animation-delay:0.8s]">
          <div className="relative inline-block">
            <div className="w-32 h-32 mx-auto bg-gradient-dark-green rounded-full flex items-center justify-center shadow-floating elegant-glow">
              <svg width="60" height="60" viewBox="0 0 60 60" className="text-gold fill-current">
                {/* Tea cup */}
                <path d="M10 30 Q10 25 15 25 L35 25 Q40 25 40 30 L40 45 Q40 50 35 50 L15 50 Q10 50 10 45 Z" />
                <path d="M40 30 Q45 30 45 35 Q45 40 40 40" fill="none" stroke="currentColor" strokeWidth="2" />
                {/* Handle */}
                <circle cx="25" cy="37" r="8" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.5" />
              </svg>
            </div>
            
            {/* Steam Animation */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-8 bg-gold-glow rounded-full opacity-60 steam"
                  style={{
                    left: `${-8 + i * 8}px`,
                    animationDelay: `${i * 0.5}s`,
                    animationDuration: `${3 + i * 0.5}s`,
                  }}
                />
              ))}
            </div>
          </div>
          
          <p className="text-background/80 mt-8 text-lg font-medium">
            Jouw dagelijkse moment van rust en reflectie ✨
          </p>
        </div>

        {/* Premium Lotus Background */}
        <div className="relative max-w-4xl mx-auto animate-on-scroll [animation-delay:1s]">
          <div className="relative overflow-hidden rounded-3xl elegant-glow">
            <img
              src={lotusImage}
              alt="Blue Lotus Flower"
              className="w-full h-64 object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/50 to-transparent" />
            
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-background">
                <h3 className="font-serif text-2xl font-bold mb-2 text-gold">
                  "Elke slok brengt me dichter bij mijn innerlijke rust"
                </h3>
                <p className="text-background/80">- Sarah, trouwe klant</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};